<script>
import { mapGetters } from "vuex";

import leave from "@/mixins/leave";
import programService from "@/services/program";
import formService from "@/services/form";
import Spinner from "@/components/ui/Spinner";
import FormRenderer from "@/components/ui/FormRenderer";

export default {
  name: "ModalProgram",
  mixins: [leave],

  components: {
    Spinner,
    FormRenderer,
  },

  props: {
    showDialogModal: {
      type: Boolean,
    },
    currentProgram: {
      type: Object,
    },
  },

  data() {
    return {
      isLoading: false,
      isPosting: false,
      programId: null,
      program: {
        name: "",
        description: "",
        requiresApproval: false,
        isActive: true,
        initialData: [],
      },
      originalProgram: null,
      selectedFormId: "",
      forms: [],
      isFetchingProgram: false,
      isUpdate: !!this.currentProgram?._id,
      isShowed: this.showDialogModal,
    };
  },

  computed: {
    ...mapGetters(["isAdmin", "isDoctor"]),
  },

  async created() {
    await this.loadForms();

    if (this.isUpdate) {
      this.originalProgram = JSON.parse(JSON.stringify(this.currentProgram));
      this.program = JSON.parse(JSON.stringify(this.currentProgram));
    }
  },

  mounted() {
    document.addEventListener("keyup", this.escape);
  },

  beforeDestroy() {
    document.removeEventListener("keyup", this.escape);
  },

  methods: {
    escape(event) {
      if (event.keyCode == 27) this.close();
    },

    submit() {
      if (this.isUpdate) {
        this.updateProgram();
      } else {
        this.createProgram();
      }
    },

    createProgram() {
      this.isPosting = true;

      const programData = {
        ...this.program,
        initialData: this.program.initialData.map((form) => form._id),
      };

      programService
        .createProgram(programData)
        .then((program) => {
          this.programId = program._id;
          this.$bus.$emit("program-created", program);
          this.close();
        })
        .catch(() => {
          this.isPosting = false;
        });
    },

    updateProgram() {
      this.isPosting = true;

      const programData = {
        ...this.program,
        initialData: this.program.initialData.map((form) => form._id),
      };

      programService
        .updateProgram(programData)
        .then((program) => {
          this.programId = program._id;
          this.$bus.$emit("program-updated", program);
          this.close();
        })
        .catch(() => {
          this.isPosting = false;
        });
    },

    async loadForms() {
      try {
        this.forms = await formService.getForms();
      } catch (error) {
        console.error("Error al cargar formularios:", error);
        this.forms = [];
      }
    },

    onFormSelected() {
      if (!this.selectedFormId) return;

      const selectedForm = this.forms.find(
        (form) => form._id === this.selectedFormId
      );

      if (selectedForm) {
        const isAlreadySelected = this.program.initialData.some(
          (form) => form._id === selectedForm._id
        );

        if (!isAlreadySelected) {
          const formWithCollapse = { ...selectedForm, isExpanded: false };
          this.program.initialData.push(formWithCollapse);
        }

        this.selectedFormId = "";
      }
    },

    removeForm(formToRemove) {
      this.program.initialData = this.program.initialData.filter(
        (form) => form._id !== formToRemove._id
      );
    },

    resetForm() {
      if (this.isUpdate && this.originalProgram) {
        this.program = JSON.parse(JSON.stringify(this.originalProgram));
      } else {
        this.program = {
          name: "",
          description: "",
          requiresApproval: false,
          isActive: true,
          initialData: [],
        };
      }
      this.selectedFormId = "";
    },

    close() {
      this.isPosting = false;
      this.isShowed = false;
      this.resetForm();
      this.$emit("close");
    },
  },
};
</script>

<template lang="pug">
el-dialog(name="modal-program", :visible.sync="isShowed", :before-close="close", :title="isUpdate ? 'Editar programa' : 'Crear programa'")
  .el-dialog__body
    ValidationObserver(v-slot="{handleSubmit}")
      form(@submit.prevent="handleSubmit(submit)")
        label.label * Nombre
        ValidationProvider(name="Nombre" rules="required" v-slot="{ errors }")
          el-input(v-model="program.name")
          span.has-error {{errors[0]}}

        label.label * Descripción
        ValidationProvider(name="Description" rules="required" v-slot="{ errors }")
          el-input(type="textarea" v-model="program.description" :rows="3")
          span.has-error {{errors[0]}}


        label.label Requiere Aprobación
        el-checkbox(v-model="program.requiresApproval")
          | El programa requiere aprobación antes de ser asignado

        label.label Estado
        el-checkbox(v-model="program.isActive")
          | Programa activo
        
        label.label * Formularios Iniciales
        el-select(
          v-model="selectedFormId"
          @change="onFormSelected"
          placeholder="Seleccionar formulario"
          style="width: 100%"
          clearable
          :loading="forms.length === 0"
        )
          el-option(
            v-for="form in forms"
            :key="form._id"
            :label="form.name || form.title || form.description"
            :value="form._id"
          )
        
        .selected-forms(v-if="program.initialData.length > 0")
          .selected-form(
            v-for="form in program.initialData"
            :key="form._id"
          )
            el-collapse(v-model="form.isExpanded")
              el-collapse-item(:name="form._id")
                template(slot="title")
                  span.form-title {{ form.name || form.title }}
                  el-button.form-delete-btn(
                    type="text"
                    size="mini"
                    @click.stop="removeForm(form)"
                    icon="el-icon-delete"
                    title="Eliminar formulario"
                  )
                FormRenderer(
                  :selected-form="form"
                  :is-visible="true"
                  :readonly="true"
                  :patient="null"
                  :hide-header="true"
                )

        span(v-if="isUpdate")
          label.label ID
          el-input(v-model="program._id" readonly)

  .el-dialog__footer
    el-button.button(type="info" @click="close()") Cancelar
    el-button.button(type="primary" :loading="isPosting" @click="submit") Guardar
</template>

<style lang="scss" scoped>
.el-dialog__body {
  padding: 0 20px 20px 20px;
}

.label {
  margin-top: 10px;
}

.location {
  .micon {
    margin-right: 10px;
  }
}

.modal {
  z-index: 9999;
}

.event-detail-modal {
  h2 {
    font-weight: bold;
    margin-bottom: 20px;
  }

  h3 {
    margin: 0 0 20px;
  }

  .form-item {
    display: flex;
    flex-direction: column;
    margin-bottom: 30px;

    .description {
      word-break: break-word;
      font-weight: bold;
      color: $primary;
      margin-bottom: 10px;
    }

    .response {
      margin-left: 20px;
      display: list-item;
      list-style-type: "→ ";
    }
  }

  .view-task {
    padding: 1rem;
  }
}

.el-dialog__footer {
  display: flex;
  gap: 10px;
  justify-content: flex-end;

  .button {
    margin: 0;
  }
}

.selected-forms {
  margin-top: 15px;
}

.selected-form {
  margin-bottom: 5px;
  border: 1px solid #e4e7ed;
  border-radius: 7px;
  overflow: hidden;

  &:last-child {
    margin-bottom: 0;
  }
}

.form-title {
  flex: 1;
  font-weight: 500;
  color: #303133;
}

.form-delete-btn {
  padding: 10px;
  color: #f56c6c;
  border-radius: 10px;

  &:hover {
    color: #f56c6c;
    background-color: #fef0f0;
  }
}

.selected-form .form-container {
  margin-top: 0;
  border: none;
  border-radius: 0;
}

.selected-form .el-collapse-item__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
}

.selected-form .el-collapse-item__wrap {
  border-bottom: none;
}
</style>

<style lang="scss">
.selected-form .el-collapse-item__header {
  background-color: #fff !important;
  color: #303133 !important;
}
</style>
